import { MeResponse } from "@retrospecter/shared/types";

export class UnauthorizedError extends Error {
  constructor(message = "Authentication required") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends Error {
  constructor(message = "Access forbidden") {
    super(message);
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends Error {
  constructor(message = "Not found") {
    super(message);
    this.name = "NotFoundError";
  }
}

export class ConflictError extends Error {
  constructor(message = "Conflict") {
    super(message);
    this.name = "ConflictError";
  }
}

/** Auth provider reachable but failing (HTTP 502) — distinct from bad credentials. */
export class AuthProviderUnavailableError extends Error {
  constructor(message = "Authentication service unavailable") {
    super(message);
    this.name = "AuthProviderUnavailableError";
  }
}

let csrfToken: string | null = null;

/**
 * Drops the cached CSRF token. The server rotates a session's CSRF secret when
 * the session's identity changes (logging in, logging out), which leaves any
 * previously cached token invalid.
 */
export function clearCsrfToken(): void {
  csrfToken = null;
}

export async function getCsrfToken(): Promise<string> {
  if (!csrfToken) {
    const response = await fetch("/api/auth/me", {
      credentials: "include",
    });
    const data: MeResponse = await response.json();
    if (data.csrfToken) {
      csrfToken = data.csrfToken;
    }
  }
  return csrfToken || "";
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export async function apiCall(
  path: string,
  options: RequestInit & { method?: string } = {}
): Promise<any> {
  const method = options.method || "GET";
  const isMutation = !SAFE_METHODS.has(method);

  const send = async () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    if (isMutation) {
      const token = await getCsrfToken();
      if (token) {
        headers["X-CSRF-Token"] = token;
      }
    }

    return fetch(path, { ...options, method, headers, credentials: "include" });
  };

  let response = await send();

  // A rejected CSRF token usually means the session rotated underneath us.
  // Refresh once and retry before surfacing an error to the caller.
  if (response.status === 403 && isMutation) {
    const peek = await response.clone().json().catch(() => null);
    if (peek?.error === "csrf_token_invalid") {
      clearCsrfToken();
      response = await send();
    }
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" }));
    const detail = body.error || body.message || "API error";

    switch (response.status) {
      case 401:
        // A 401 on a mutation means our cached CSRF token belongs to a session
        // that no longer exists; drop it so the next call re-fetches.
        csrfToken = null;
        throw new UnauthorizedError(detail);
      case 403:
        if (detail === "csrf_token_invalid") {
          csrfToken = null;
        }
        throw new ForbiddenError(detail);
      case 404:
        throw new NotFoundError(detail);
      case 409:
        throw new ConflictError(detail);
      case 502:
        throw new AuthProviderUnavailableError(detail);
      default:
        throw new Error(detail);
    }
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export async function apiGet(path: string) {
  return apiCall(path, { method: "GET" });
}

export async function apiPost(path: string, body: any) {
  return apiCall(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function apiPatch(path: string, body: any) {
  return apiCall(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function apiDelete(path: string) {
  return apiCall(path, { method: "DELETE" });
}
